import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  ArrowRight,
  Phone,
  ChevronDown,
  UserCog,
  Plane,
  Users,
  BadgeCheck,
  TrendingDown,
  Receipt,
  ShieldCheck,
  Car,
  Headset,
  MailCheck,
} from "lucide-react";
import Layout from "@/components/Layout";
import PricingConversionCTA from "@/components/pricing/PricingConversionCTA";

const API = process.env.REACT_APP_BACKEND_URL;

/* ---------- JSON-LD ---------- */
const ITEM_LIST_LD = {
  "@context": "https://schema.org",
  "@type": "ItemList",
  name: "Phantasm Limousine Service Rates",
  description:
    "Locked flat-rate chauffeured car service rates across Boston, Logan Airport, Cape Cod, and NYC.",
  itemListElement: [
    {
      "@type": "Service",
      position: 1,
      name: "Business Sedan Hourly Charter",
      description: "Boston hourly chauffeur service, 3-hour minimum.",
      provider: {
        "@type": "LocalBusiness",
        name: "Phantasm Limousine Service Inc.",
        areaServed: ["Boston, MA", "New York, NY"],
        telephone: "+1-347-523-0763",
      },
      offers: {
        "@type": "Offer",
        price: "119.00",
        priceCurrency: "USD",
        priceSpecification: {
          "@type": "UnitPriceSpecification",
          unitCode: "HUR",
          price: "119.00",
          priceCurrency: "USD",
        },
      },
    },
    {
      "@type": "Service",
      position: 2,
      name: "Luxury SUV Hourly Charter",
      description: "Cadillac Escalade ESV class hourly service, 3-hour minimum.",
      offers: { "@type": "Offer", price: "143.00", priceCurrency: "USD" },
    },
    {
      "@type": "Service",
      position: 3,
      name: "Executive Sprinter Van Hourly Charter",
      description: "Mercedes-Benz Sprinter Executive group transport, 3-hour minimum.",
      offers: { "@type": "Offer", price: "428.00", priceCurrency: "USD" },
    },
  ],
};

const FAQS = [
  {
    q: "How much does a Boston limo service cost?",
    a: "Displayed Phantasm rates start at $119 per hour for the Business Sedan, $143 per hour for the Luxury SUV, and $428 per hour for the Executive Sprinter Van, each with a three-hour minimum. Airport transfers and point-to-point routes are quoted flat; ranges reflect the pickup neighbourhood and time of day. Your final locked quote is generated at booking based on trip details.",
  },
  {
    q: "How much is Logan Airport transportation?",
    a: "Business Sedan transfers from Downtown Boston to Logan start at $119. Luxury SUV transfers from the same pickup start at $143. Suburban pickups (Newton, Wellesley, Dedham) begin at $124–$143 for the sedan. Every airport route includes flight tracking, curbside meet-and-greet, and toll coverage; there is no separate airport surcharge line-item on your invoice.",
  },
  {
    q: "Are Phantasm rates flat-rate?",
    a: "Yes. Every reservation is locked at the moment you book. We do not apply surge pricing, dynamic multipliers, or route-length surprises. Where a range is shown, it accounts for pickup zone and time-of-day; once you confirm the specific pickup and datetime, one price is locked and honoured for the duration of your reservation.",
  },
  {
    q: "Do rates include tolls?",
    a: "Yes. Massachusetts tolls (Tobin, Sumner, Ted Williams, Turnpike), New Hampshire tolls, and any Connecticut / New York tolls on long-distance runs are all baked into your flat quote. You will never see a toll add-on line item or receive a post-trip toll invoice.",
  },
  {
    q: "Is flight tracking included?",
    a: "Complimentary on every airport transfer. Once your reservation is confirmed, our dispatch team monitors your inbound flight in real time and shifts your chauffeur's curbside arrival window to match delays, early landings, and gate reassignments. There is no extra charge for wait time on tracked flights up to 60 minutes past the scheduled landing.",
  },
  {
    q: "How does hourly chauffeur pricing work?",
    a: "Hourly charters bill against the vehicle's per-hour rate, starting from $119 (Business Sedan), $143 (Luxury SUV), or $428 (Executive Sprinter Van). All hourly reservations require a three-hour minimum. Your chauffeur stays with you for the reserved window, waits at each stop at no additional charge, and continues to the next destination on your itinerary. Additional time beyond the reserved window is billed at the same hourly rate in 15-minute increments.",
  },
  {
    q: "How far in advance should I reserve?",
    a: "For airport transfers and standard point-to-point trips, we recommend 24 hours' notice to guarantee your preferred vehicle class. For premium slots (Friday and Saturday evenings, holiday weekends, marathon and convention windows) we recommend booking one to two weeks ahead. Same-day and last-minute requests are welcome via our 24/7 dispatch line — subject to vehicle and chauffeur availability.",
  },
];

const FAQ_LD = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: FAQS.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
};

/* ---------- Educational Copy (100–150 words each, original) ---------- */
const PRICING_GUIDE = [
  {
    title: "How Airport Pricing Works",
    body:
      "Every Phantasm airport transfer is quoted flat. The published range accounts for pickup zone (Downtown, Cambridge, MetroWest, North Shore, South Shore) and time-of-day considerations such as late-night surcharges. Once you confirm the specific pickup address and pickup datetime, one number is locked and honoured — regardless of traffic, route changes, or tolls incurred along the way. Every airport reservation includes complimentary flight tracking, curbside meet-and-greet at baggage claim on request, and toll coverage. Your chauffeur will monitor your inbound flight in real time and adjust the curbside arrival window automatically for early landings or delays. There are no surge multipliers, no post-trip toll invoices, and no separate airport surcharge on your receipt.",
  },
  {
    title: "Hourly Chauffeur Pricing",
    body:
      "Hourly charters are ideal for multi-stop itineraries, executive standby, wedding parties, event nights, and roadshow days when you need the vehicle on-call rather than point-to-point. Rates begin at $119/hr for the Business Sedan, $143/hr for the Luxury SUV, and $428/hr for the Executive Sprinter Van, each with a three-hour minimum. Within the reserved window your chauffeur waits at each stop at no additional charge, follows your itinerary, and continues to the next destination on request. Additional time beyond the reserved window is billed at the same hourly rate in fifteen-minute increments — always the same rate you saw at booking. No dynamic pricing, no premium multipliers on nights or weekends.",
  },
  {
    title: "Benefits Of Flat-Rate Transportation",
    body:
      "A flat rate is more than a price; it is a promise. It means the number quoted at booking will be honoured — even if traffic doubles your travel time, even if the route detours through a toll corridor, even if your driver has to wait at the curb for a delayed flight. It also means there is no incentive for the chauffeur to take the long way or idle in traffic. Every party understands the deliverable and the price up front. Phantasm's flat-rate policy is backed by a written confirmation email sent within seconds of booking. If your itinerary needs to change mid-trip, our dispatch team re-quotes on the spot with full transparency, so there are no receipt surprises.",
  },
  {
    title: "Vehicle Selection Guidance",
    body:
      "The Business Sedan seats up to three passengers with three carry-ons and is the daily choice for executive airport transfers, solo corporate travel, and business meetings. The Luxury SUV — currently the Cadillac Escalade ESV class — seats up to six with generous luggage capacity, and is preferred for families arriving on international flights, small wedding parties, and premium client hosting. The Executive Sprinter Van seats seven to fourteen in first-class-style captain's chairs and is purpose-built for group airport transfers, corporate shuttles, and event convoys. If you're unsure which class fits your itinerary, our dispatch team will recommend the right vehicle based on passenger count, luggage volume, and route.",
  },
  {
    title: "Business Travel Options",
    body:
      "Phantasm operates a dedicated corporate desk for repeat business travellers, C-suite standby, roadshows, and multi-city itineraries across the Northeast corridor. Corporate accounts include consolidated monthly invoicing, direct-billed reservations without out-of-pocket driver interaction, real-time trip receipts to the traveller and administrator, and priority routing on high-demand dates. Every corporate reservation is chauffeured by a professionally licensed driver in a late-model luxury vehicle, with full commercial insurance, a groomed presentation, and English fluency as a hard baseline. For high-security or discreet-transit needs, our chauffeurs are trained in defensive driving and confidentiality protocols and can be briefed on specific traveller preferences ahead of arrival.",
  },
  {
    title: "Event Transportation Options",
    body:
      "For weddings, milestone birthdays, corporate galas, cruise embarkations, sporting events, and concert nights, Phantasm offers coordinated event transportation with a single point of contact. Book a single Luxury SUV for the bride's arrival, an Executive Sprinter Van for the wedding party, or a coordinated fleet across pickups and drop-offs — our dispatch team builds and monitors the timeline on your behalf. Every event reservation includes on-site chauffeur coordination, alternate-route planning around road closures and parade blocks, and a dedicated dispatcher assigned to your event window. Event pricing typically follows the hourly model; multi-vehicle bookings are eligible for a fleet-coordination rate — call our dispatch line to structure the itinerary.",
  },
];

const INCLUDED = [
  { icon: UserCog, label: "Professional Chauffeur" },
  { icon: Plane, label: "Flight Tracking" },
  { icon: Users, label: "Meet & Greet Service" },
  { icon: BadgeCheck, label: "Flat-Rate Pricing" },
  { icon: TrendingDown, label: "No Surge Pricing" },
  { icon: Receipt, label: "Toll Management" },
  { icon: ShieldCheck, label: "Commercial Insurance" },
  { icon: Car, label: "Luxury Vehicles" },
  { icon: Headset, label: "24/7 Dispatch Team" },
  { icon: MailCheck, label: "Instant Booking Confirmation" },
];

const HOURLY_USE_CASES = {
  "Business Sedan": [
    "Executive airport transfers",
    "Solo corporate travel",
    "Point-to-point Boston routes",
    "Corporate meetings",
    "Professional licensed chauffeur",
  ],
  "Luxury SUV": [
    "Family airport arrivals",
    "Wedding & event transportation",
    "Corporate group travel",
    "Premium leather interior",
    "Extra luggage capacity",
  ],
  "Executive Sprinter Van": [
    "Group airport transfers",
    "Corporate shuttles & events",
    "University move-in weekends",
    "Wedding party transportation",
    "Conference & summit transfers",
  ],
};

/* ---------- Page ---------- */
export default function Pricing() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetch(`${API}/api/pricing/marketing`)
      .then((r) => r.json())
      .then(setData)
      .catch((e) => setError(String(e)));
  }, []);

  return (
    <Layout>
      <Helmet>
        <title>
          Boston Limo Service Pricing | Logan Airport Rates &amp; Chauffeur Service | Phantasm Limousine
        </title>
        <meta
          name="description"
          content="Transparent Boston limo pricing with Logan Airport transfers, hourly chauffeur service, long-distance transportation, and luxury vehicle options. Get an instant quote today."
        />
        <meta
          name="keywords"
          content="boston car service rates, logan airport black car, boston chauffeur pricing, logan private transfer quote, cape cod car service, boston to nyc chauffeur"
        />
        <meta
          property="og:title"
          content="Boston Limo Service Pricing | Logan Airport Rates & Chauffeur Service | Phantasm Limousine"
        />
        <meta
          property="og:description"
          content="Transparent Boston limo pricing with Logan Airport transfers, hourly chauffeur service, long-distance transportation, and luxury vehicle options. Get an instant quote today."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.phantasmlimousineservice.com/pricing" />
        <link rel="canonical" href="https://www.phantasmlimousineservice.com/pricing" />
        <script type="application/ld+json">{JSON.stringify(ITEM_LIST_LD)}</script>
        <script type="application/ld+json">{JSON.stringify(FAQ_LD)}</script>
      </Helmet>

      {/* HERO */}
      <section
        data-testid="pricing-hero"
        className="relative pt-40 pb-16 md:pt-48 md:pb-20 border-b border-white/10 bg-[#0B0B0C]"
      >
        <div className="max-w-5xl mx-auto px-6 md:px-12 text-center">
          <div className="text-[11px] uppercase tracking-[0.4em] text-[#C5A24A] mb-6">
            Boston Chauffeur Rate Card
          </div>
          <h1
            data-testid="pricing-headline"
            className="font-serif text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tighter text-white leading-[1.05]"
          >
            Transparent Rates. Locked Quotes. No Surge.
          </h1>
          <p className="mt-8 max-w-2xl mx-auto text-sm md:text-base text-[#E6E6E6] font-light leading-relaxed">
            Published starting rates for every vehicle class across popular Boston Logan
            routes, hourly chauffeur service, and long-distance transportation. Every trip
            is quoted flat at booking.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/book"
              data-testid="pricing-hero-book"
              className="inline-flex items-center gap-2 bg-white text-black hover:bg-[#E6E6E6] px-9 py-4 transition-all duration-300 font-medium text-xs tracking-[0.28em] uppercase"
            >
              Book Now <ArrowRight size={14} strokeWidth={1.5} />
            </Link>
            <a
              href="tel:+13475230763"
              data-testid="pricing-hero-call"
              className="inline-flex items-center gap-2 bg-transparent border border-white/30 text-white hover:bg-white hover:text-black px-9 py-4 transition-all duration-300 font-medium text-xs tracking-[0.28em] uppercase"
            >
              <Phone size={14} strokeWidth={1.5} /> Call +1-347-523-0763
            </a>
          </div>
        </div>
      </section>

      {/* SECTION 1 — Popular Logan Airport Routes */}
      <SectionTitle
        eyebrow="Section 01"
        heading="Popular Logan Airport Routes"
        sub="Fixed-quote transfers to Boston Logan International (BOS). Every trip includes flight tracking, tolls, and a 20% auto-gratuity."
        testid="pricing-logan-section"
      />
      <div className="max-w-6xl mx-auto px-6 md:px-12 pb-12 space-y-4" data-testid="logan-regions">
        {data &&
          Object.entries(data.logan_routes).map(([region, rows], idx) => (
            <RegionCollapsible
              key={region}
              region={region}
              rows={rows}
              defaultOpen={idx === 0}
            />
          ))}
        {error && (
          <div className="text-sm text-red-300/90 font-light">
            Rate matrix unavailable — please call our dispatch on{" "}
            <a href="tel:+13475230763" className="text-white underline">
              +1-347-523-0763
            </a>
            .
          </div>
        )}
      </div>
      <PricingConversionCTA testid="cta-after-logan" />

      {/* SECTION 2 — Long Distance */}
      <SectionTitle
        eyebrow="Section 02"
        heading="Long Distance Transportation"
        sub="Regional and intercity chauffeur transfers. Every route quoted flat, tolls included."
        testid="pricing-long-distance-section"
        alt
      />
      <div className="max-w-6xl mx-auto px-6 md:px-12 pb-12">
        <div className="backdrop-blur-xl bg-[#121214]/70 border border-white/10">
          {/* Desktop table */}
          <div className="hidden md:block">
            <table className="w-full text-left" data-testid="long-distance-table">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-[10px] uppercase tracking-[0.32em] text-[#C5A24A] px-6 py-5">Route</th>
                  <th className="text-[10px] uppercase tracking-[0.32em] text-[#C5A24A] px-6 py-5 text-right">Business Sedan</th>
                  <th className="text-[10px] uppercase tracking-[0.32em] text-[#C5A24A] px-6 py-5 text-right">Luxury SUV</th>
                  <th className="text-[10px] uppercase tracking-[0.32em] text-[#C5A24A] px-6 py-5">ETA</th>
                  <th className="text-[10px] uppercase tracking-[0.32em] text-[#C5A24A] px-6 py-5 text-right">Reserve</th>
                </tr>
              </thead>
              <tbody>
                {(data?.long_distance || []).map((r) => (
                  <tr
                    key={r.route}
                    data-testid={`long-distance-row-${slug(r.route)}`}
                    className="border-b border-white/[0.06] last:border-b-0 hover:bg-white/[0.02] transition-colors"
                  >
                    <td className="px-6 py-6 font-serif text-lg text-white tracking-tight">
                      <div>{r.route}</div>
                      <div className="mt-1.5">
                        <span className="text-[10px] uppercase tracking-[0.24em] text-[#C5A24A] border border-[#C5A24A]/40 px-2 py-0.5">
                          Flat-Rate
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-6 text-right text-sm text-[#E6E6E6] font-light tabular-nums">{r.sedan}</td>
                    <td className="px-6 py-6 text-right text-sm text-[#E6E6E6] font-light tabular-nums">{r.suv}</td>
                    <td className="px-6 py-6 text-sm text-[#8E9AA6] font-light">{r.eta}</td>
                    <td className="px-6 py-6 text-right">
                      <div className="inline-flex gap-2">
                        <Link
                          to={`/book?pickup=${encodeURIComponent(r.route.split("→")[0].trim())}&dropoff=${encodeURIComponent(r.route.split("→")[1].trim())}`}
                          className="text-[10px] uppercase tracking-[0.24em] border border-white/30 hover:bg-white hover:text-black text-white px-3 py-2"
                        >
                          Book
                        </Link>
                        <a href="tel:+13475230763" className="text-[10px] uppercase tracking-[0.24em] border border-[#C5A24A]/50 text-[#C5A24A] hover:bg-[#C5A24A] hover:text-black px-3 py-2">
                          Call
                        </a>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {/* Mobile stacked */}
          <div className="md:hidden divide-y divide-white/[0.06]">
            {(data?.long_distance || []).map((r) => (
              <div key={r.route} data-testid={`long-distance-row-${slug(r.route)}-mobile`} className="px-5 py-6">
                <div className="font-serif text-base text-white leading-snug mb-3 tracking-tight">
                  {r.route}
                </div>
                <div className="space-y-2 text-sm">
                  <Row label="Business Sedan" value={r.sedan} />
                  <Row label="Luxury SUV" value={r.suv} />
                  <Row label="ETA" value={r.eta} />
                </div>
                <div className="mt-4 flex gap-2">
                  <Link
                    to={`/book?pickup=${encodeURIComponent(r.route.split("→")[0].trim())}&dropoff=${encodeURIComponent(r.route.split("→")[1].trim())}`}
                    className="text-[10px] uppercase tracking-[0.24em] border border-white/30 hover:bg-white hover:text-black text-white px-3 py-2"
                  >
                    Book
                  </Link>
                  <a href="tel:+13475230763" className="text-[10px] uppercase tracking-[0.24em] border border-[#C5A24A]/50 text-[#C5A24A] px-3 py-2">
                    Call
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <PricingConversionCTA testid="cta-after-long-distance" />

      {/* SECTION 3 — Hourly */}
      <SectionTitle
        eyebrow="Section 03"
        heading="Hourly Chauffeur Service"
        sub="For multi-stop itineraries, event nights, weddings, and executive standby. Three-hour minimum."
        testid="pricing-hourly-section"
      />
      <div
        className="max-w-6xl mx-auto px-6 md:px-12 pb-12 grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-7"
        data-testid="hourly-cards"
      >
        {(data?.hourly || []).map((c) => (
          <div
            key={c.class}
            data-testid={`hourly-card-${slug(c.class)}`}
            className="backdrop-blur-xl bg-[#121214]/70 border border-white/10 hover:border-[#C5A24A]/45 transition-colors flex flex-col"
          >
            <div className="px-7 pt-8 pb-6 border-b border-white/[0.06]">
              <div className="text-[10px] uppercase tracking-[0.32em] text-[#8E9AA6] border border-white/15 inline-block px-2 py-1 mb-6">
                {c.min_hours}-HR MINIMUM · {c.capacity} PAX
              </div>
              <h3 className="font-serif text-2xl md:text-3xl text-white tracking-tight mb-6">
                {c.class}
              </h3>
              <div className="flex items-baseline gap-2">
                <span className="font-serif text-5xl md:text-6xl font-semibold tracking-tighter text-white leading-none">
                  ${c.hourly_from}
                </span>
                <span className="text-xs uppercase tracking-[0.32em] text-[#8E9AA6]">/ HR</span>
              </div>
            </div>
            <ul className="px-7 py-6 space-y-2.5 text-sm text-[#E6E6E6]/80 font-light">
              {(HOURLY_USE_CASES[c.class] || []).map((u) => (
                <li key={u} className="flex items-start gap-2.5">
                  <span className="mt-1.5 w-1 h-1 bg-[#C5A24A] rounded-full shrink-0" aria-hidden />
                  <span>{u}</span>
                </li>
              ))}
            </ul>
            <div className="px-7 pb-7 mt-auto flex gap-2">
              <Link
                to="/book?service=hourly"
                data-testid={`hourly-card-${slug(c.class)}-book`}
                className="flex-1 inline-flex items-center justify-center gap-2 bg-white text-black hover:bg-[#E6E6E6] px-4 py-3 text-[10.5px] uppercase tracking-[0.28em]"
              >
                Book Now
              </Link>
              <a
                href="tel:+13475230763"
                data-testid={`hourly-card-${slug(c.class)}-call`}
                className="flex-1 inline-flex items-center justify-center gap-2 border border-[#C5A24A]/50 text-[#C5A24A] hover:bg-[#C5A24A] hover:text-black px-4 py-3 text-[10.5px] uppercase tracking-[0.28em]"
              >
                Call Now
              </a>
            </div>
          </div>
        ))}
      </div>
      <PricingConversionCTA testid="cta-after-hourly" />

      {/* SECTION 4 — What Is Included */}
      <SectionTitle
        eyebrow="Section 04"
        heading="What Is Included"
        sub="Every Phantasm reservation ships with the same standards, regardless of vehicle class or route."
        testid="pricing-included-section"
        alt
      />
      <div className="max-w-6xl mx-auto px-6 md:px-12 pb-12">
        <div
          className="grid grid-cols-2 md:grid-cols-5 gap-3 md:gap-4"
          data-testid="included-grid"
        >
          {INCLUDED.map((f) => {
            const Icon = f.icon;
            return (
              <div
                key={f.label}
                data-testid={`included-item-${slug(f.label)}`}
                className="backdrop-blur-xl bg-[#121214]/70 border border-white/10 hover:border-[#C5A24A]/40 transition-colors px-4 py-6 flex flex-col items-start gap-3"
              >
                <div className="w-10 h-10 border border-[#C5A24A]/45 bg-[#C5A24A]/[0.06] flex items-center justify-center text-[#C5A24A]">
                  <Icon size={17} strokeWidth={1.5} />
                </div>
                <div className="text-[11px] md:text-xs uppercase tracking-[0.16em] text-[#E6E6E6] leading-snug">
                  {f.label}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <PricingConversionCTA testid="cta-after-included" variant="compact" headline="Reserve With Confidence." />

      {/* SECTION 5 — Boston Chauffeur Pricing Guide */}
      <SectionTitle
        eyebrow="Section 05"
        heading="Boston Chauffeur Pricing Guide"
        sub="Six short reads explaining exactly how Phantasm quotes are structured — no jargon, no fluff."
        testid="pricing-guide-section"
      />
      <div className="max-w-6xl mx-auto px-6 md:px-12 pb-12">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6" data-testid="pricing-guide">
          {PRICING_GUIDE.map((g) => (
            <article
              key={g.title}
              data-testid={`pricing-guide-${slug(g.title)}`}
              className="backdrop-blur-xl bg-[#121214]/70 border border-white/10 px-6 md:px-7 py-6 md:py-7"
            >
              <div className="text-[10px] uppercase tracking-[0.32em] text-[#C5A24A] mb-3">
                Guide
              </div>
              <h4 className="font-serif text-xl md:text-2xl text-white tracking-tight mb-4 leading-snug">
                {g.title}
              </h4>
              <p className="text-sm text-[#E6E6E6]/80 font-light leading-relaxed">{g.body}</p>
            </article>
          ))}
        </div>
      </div>
      <PricingConversionCTA testid="cta-after-guide" />

      {/* SECTION 6 — FAQ */}
      <SectionTitle
        eyebrow="Section 06"
        heading="Frequently Asked"
        sub="Direct answers to the seven questions our dispatch team hears most often."
        testid="pricing-faq-section"
        alt
      />
      <div className="max-w-4xl mx-auto px-6 md:px-12 pb-12 space-y-3" data-testid="faq-list">
        {FAQS.map((f, i) => (
          <FAQItem key={f.q} q={f.q} a={f.a} testid={`faq-${i + 1}`} />
        ))}
      </div>
      <PricingConversionCTA testid="cta-after-faq" />

      {/* DISCLAIMER */}
      <section data-testid="pricing-disclaimer" className="bg-[#0B0B0C] py-12">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <div className="border-l-2 border-[#C5A24A] pl-5 py-2">
            <div className="text-[10px] uppercase tracking-[0.4em] text-[#C5A24A] mb-2">Disclaimer</div>
            <p className="text-sm text-[#E6E6E6]/85 font-light leading-relaxed">
              Displayed prices are estimates for common routes. Final pricing is confirmed at
              booking based on trip details.
            </p>
          </div>
        </div>
      </section>
    </Layout>
  );
}

/* ---------- Sub-components ---------- */
function slug(s) {
  return s
    .toLowerCase()
    .replace(/[→↔/&,()]/g, " ")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
}

function SectionTitle({ eyebrow, heading, sub, testid, alt = false }) {
  return (
    <section
      data-testid={testid}
      className={`relative border-t border-white/10 py-16 md:py-20 ${
        alt ? "bg-[#121214]" : "bg-[#0B0B0C]"
      }`}
    >
      <div className="max-w-6xl mx-auto px-6 md:px-12">
        <div className="text-[10px] uppercase tracking-[0.4em] text-[#C5A24A] mb-4">
          {eyebrow}
        </div>
        <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl font-semibold tracking-tight text-white">
          {heading}
        </h2>
        {sub && (
          <p className="mt-4 max-w-2xl text-sm md:text-base text-[#8E9AA6] font-light leading-relaxed">
            {sub}
          </p>
        )}
      </div>
    </section>
  );
}

function RegionCollapsible({ region, rows, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  const tid = `logan-region-${slug(region)}`;
  return (
    <div
      data-testid={tid}
      className="backdrop-blur-xl bg-[#121214]/70 border border-white/10"
    >
      <button
        type="button"
        aria-expanded={open}
        data-testid={`${tid}-toggle`}
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-4 px-5 md:px-7 py-5 text-left hover:bg-white/[0.02] transition-colors"
      >
        <div>
          <div className="text-[10px] uppercase tracking-[0.32em] text-[#C5A24A] mb-1">
            Region
          </div>
          <div className="font-serif text-xl md:text-2xl text-white tracking-tight">
            {region}
          </div>
        </div>
        <div className="flex items-center gap-4">
          <span className="hidden sm:inline text-[10px] uppercase tracking-[0.28em] text-[#8E9AA6]">
            {rows.length} pickups
          </span>
          <ChevronDown
            size={20}
            strokeWidth={1.5}
            className={`text-[#8E9AA6] transition-transform duration-300 ${
              open ? "rotate-180" : ""
            }`}
          />
        </div>
      </button>
      {open && (
        <ul className="border-t border-white/[0.06] divide-y divide-white/[0.06]">
          {rows.map((r) => (
            <li
              key={r.pickup}
              data-testid={`${tid}-row-${slug(r.pickup)}`}
              className="grid grid-cols-1 md:grid-cols-[2fr_1fr_1fr_1fr_auto] gap-3 md:gap-5 items-center px-5 md:px-7 py-5"
            >
              <div>
                <div className="font-serif text-base md:text-lg text-white leading-snug tracking-tight">
                  {r.pickup} ↔ Logan
                </div>
                <div className="text-[10px] uppercase tracking-[0.28em] text-[#8E9AA6] mt-1">
                  Flat-Rate · Tolls Included · 1–3 / 1–6 / 7–12 pax
                </div>
              </div>
              <PriceCell label="Sedan" value={r.sedan} />
              <PriceCell label="SUV" value={r.suv} />
              <PriceCell label="Van" value={r.van} />
              <div className="flex gap-2 md:justify-end">
                <Link
                  to={`/book?pickup=${encodeURIComponent(`${r.pickup}, MA`)}&dropoff=${encodeURIComponent(
                    "Boston Logan International Airport (BOS)",
                  )}`}
                  className="text-[10px] uppercase tracking-[0.24em] border border-white/30 hover:bg-white hover:text-black text-white px-3 py-2"
                >
                  Book
                </Link>
                <a
                  href="tel:+13475230763"
                  className="text-[10px] uppercase tracking-[0.24em] border border-[#C5A24A]/50 text-[#C5A24A] hover:bg-[#C5A24A] hover:text-black px-3 py-2"
                >
                  Call
                </a>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function PriceCell({ label, value }) {
  return (
    <div>
      <div className="text-[9px] uppercase tracking-[0.28em] text-[#8E9AA6] mb-0.5">
        {label}
      </div>
      <div className="text-sm text-[#E6E6E6] tabular-nums font-light">{value}</div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-baseline justify-between gap-4">
      <dt className="text-[10px] uppercase tracking-[0.28em] text-[#8E9AA6]">{label}</dt>
      <dd className="text-sm text-[#E6E6E6] font-light tabular-nums text-right">{value}</dd>
    </div>
  );
}

function FAQItem({ q, a, testid }) {
  const [open, setOpen] = useState(false);
  return (
    <div
      data-testid={testid}
      className="backdrop-blur-xl bg-[#121214]/70 border border-white/10"
    >
      <button
        type="button"
        aria-expanded={open}
        data-testid={`${testid}-toggle`}
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-4 px-5 md:px-7 py-5 text-left hover:bg-white/[0.02] transition-colors"
      >
        <span className="font-serif text-base md:text-lg text-white leading-snug tracking-tight pr-4">
          {q}
        </span>
        <ChevronDown
          size={18}
          strokeWidth={1.5}
          className={`shrink-0 text-[#8E9AA6] transition-transform duration-300 ${
            open ? "rotate-180" : ""
          }`}
        />
      </button>
      {open && (
        <div className="px-5 md:px-7 pb-6 pt-1 border-t border-white/[0.06]">
          <p className="text-sm text-[#E6E6E6]/85 font-light leading-relaxed">{a}</p>
        </div>
      )}
    </div>
  );
}
